import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "react-router-dom";
import TrackedLink from "@/components/TrackedLink";
import { motion } from "framer-motion";
import { ArrowRight, RefreshCw, CreditCard, Users, Globe, Smartphone } from "lucide-react";
import { providers, usps } from "@/data/mobiel";
import logoKpn from "@/assets/logo-kpn.png";
import logoVodafone from "@/assets/logo-vodafone.png";
import logoOdido from "@/assets/logo-odido.webp";
import mobielHeroBg from "@/assets/mobiel-hero-bg.jpg";
import mobielHeroVideo from "@/assets/mobiel-hero-video.mp4";

const logos: Record<string, string> = {
  kpn: logoKpn,
  vodafone: logoVodafone,
  odido: logoOdido,
};

const uspIcons = [RefreshCw, CreditCard, Users, Globe];

const MobielPage = () => {
  return (
    <>
      {/* Hero */}
      <section className="relative min-h-[40vh] flex items-center overflow-hidden">
        {/* Background video */}
        <video
          autoPlay
          muted
          loop
          playsInline
          poster={mobielHeroBg}
          className="absolute inset-0 h-full w-full object-cover"
        >
          <source src={mobielHeroVideo} type="video/mp4" />
        </video>

        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/30" />

        <div className="container relative z-10 py-16">
            <motion.div
              className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-white/15 backdrop-blur-sm border border-white/20"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
            >
              <Smartphone className="h-7 w-7 text-white" />
            </motion.div>
            <motion.h1
              className="text-3xl md:text-5xl font-bold text-white"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              Zakelijk mobiel abonnement voor bedrijven
            </motion.h1>
            <motion.p
              className="text-white/80 mt-2 max-w-lg"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              Flexibel bellen en internetten onderweg. Kies het netwerk dat bij jouw bedrijf past.
            </motion.p>
            <motion.div
              className="flex flex-wrap items-center gap-x-6 gap-y-2 mt-5"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.25 }}
            >
              {usps.map((usp, i) => (
                <div key={i} className="flex items-center gap-2">
                  <span className={`h-2 w-2 rounded-full ${i === 0 ? "bg-primary" : i === 1 ? "bg-rinqel-blue" : i === 2 ? "bg-rinqel-green" : "bg-primary"}`} />
                  <span className="text-white/90 text-sm font-medium">{usp.titel}</span>
                </div>
              ))}
            </motion.div>
        </div>
      </section>

      {/* USPs */}
      <section className="py-16 md:py-20 bg-muted/30">
        <div className="container">
          <motion.h2
            className="text-2xl md:text-3xl font-bold text-center mb-12"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            Altijd inbegrepen bij Rinqel Mobiel
          </motion.h2>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {usps.map((usp, i) => {
              const Icon = uspIcons[i];
              const colors = [
                "text-primary bg-primary/10",
                "text-rinqel-blue bg-rinqel-blue/10",
                "text-rinqel-green bg-rinqel-green/10",
                "text-primary bg-primary/10",
              ];
              return (
                <motion.div
                  key={usp.titel}
                  className="flex flex-col items-center text-center p-6"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                >
                  <div className={`w-14 h-14 rounded-xl flex items-center justify-center mb-4 ${colors[i]}`}>
                    <Icon className="w-7 h-7" />
                  </div>
                  <h3 className="text-lg font-semibold mb-2">{usp.titel}</h3>
                  <p className="text-sm text-muted-foreground">{usp.beschrijving}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Provider Cards */}
      <section className="py-16 md:py-24">
        <div className="container">
          <motion.h2
            className="text-2xl md:text-3xl font-bold text-center mb-4"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            Kies je netwerk
          </motion.h2>
          <motion.p
            className="text-center text-muted-foreground mb-12 max-w-xl mx-auto"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            Drie topnetwerken, één aanspreekpunt. Bekijk de abonnementen per provider.
          </motion.p>

          <div className="grid gap-8 md:grid-cols-3">
            {providers.map((provider, i) => (
              <motion.div
                key={provider.slug}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.15 }}
              >
                <Card className="h-full overflow-hidden hover:shadow-lg transition-shadow border-t-4"
                  style={{ borderTopColor: provider.kleur }}
                >
                  <CardContent className="p-8 flex flex-col items-center text-center h-full">
                    <div
                      className="w-24 h-24 rounded-2xl flex items-center justify-center mb-6 p-3"
                      style={{ backgroundColor: provider.kleurLight }}
                    >
                      <img loading="lazy" decoding="async"
                        src={logos[provider.slug]}
                        alt={`${provider.naam} logo`}
                        className="w-full h-full object-contain"
                      />
                    </div>
                    <h3 className="text-2xl font-bold mb-3">{provider.naam}</h3>
                    <p className="text-muted-foreground mb-2 text-sm">
                      Vanaf <span className="font-semibold text-foreground">{provider.abonnementen[0].prijs}</span>/mnd
                    </p>
                    <p className="text-sm text-muted-foreground mb-6 flex-1">
                      {provider.beschrijving}
                    </p>
                    <Button asChild className="w-full" style={{ backgroundColor: provider.kleur }}>
                      <Link to={`/diensten/mobiel/${provider.slug}`}>
                        Bekijk abonnementen <ArrowRight className="ml-2 h-4 w-4" />
                      </Link>
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-hero-gradient text-white">
        <div className="container text-center">
          <h2 className="text-2xl md:text-3xl font-bold mb-4">Hulp bij het kiezen?</h2>
          <p className="text-white/80 mb-8 max-w-lg mx-auto">
            Wij helpen je graag met het vinden van het beste abonnement voor jouw bedrijf.
          </p>
          <Button asChild variant="hero-outline" size="lg" className="border-white text-white hover:bg-white/10">
            <TrackedLink to="/contact" trackLabel="Neem contact op" trackLocation="mobiel-cta">Neem contact op</TrackedLink>
          </Button>
        </div>
      </section>
    </>
  );
};

export default MobielPage;
